import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { RuleEditor } from './PayoutRuleSettingsPage.tsx';
import { type PayoutRule, type TournamentRuleType, validPayoutRule } from './payoutRule.ts';

interface TournamentRuleResponse {
  id: string;
  name: string;
  status: string;
  type: TournamentRuleType;
}

interface EffectiveRule {
  version: number;
  source: string;
  rule: PayoutRule;
}

export default function TournamentPayoutRulePage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<PayoutRule | null>(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const { data, error } = useQuery({
    queryKey: ['tournament', id],
    queryFn: () => api.get<TournamentRuleResponse>(`/tournament/${id}`),
    enabled: !!id,
  });
  const { data: effectiveRule, error: ruleError } = useQuery({
    queryKey: ['tournament-payout-rule', id],
    queryFn: () => api.get<EffectiveRule>(`/tournament/${id}/payout-rule`),
    enabled: !!id,
  });
  useEffect(() => {
    if (effectiveRule) setDraft(structuredClone(effectiveRule.rule));
  }, [effectiveRule]);
  const update = useMutation({
    mutationFn: () =>
      api.put(`/tournament/${id}/payout-rule`, {
        expectedVersion: effectiveRule?.version,
        reason: reason.trim(),
        rule: draft,
      }),
    onSuccess: async () => {
      setMessage('本場規則已更新；請重新試算尚未確認的結算。');
      setReason('');
      await qc.invalidateQueries({ queryKey: ['tournament', id] });
      await qc.invalidateQueries({ queryKey: ['tournament-payout-rule', id] });
    },
    onError: (failure: Error) => setMessage(failure.message),
  });
  const locked = data?.status === 'COMPLETED' || data?.status === 'CANCELLED';
  return (
    <div className="mx-auto max-w-4xl space-y-5 p-6">
      <Link to={`/tournament/${id}`} className="text-info text-sm">
        返回賽事
      </Link>
      <h1 className="text-2xl font-bold">本場獎池分配規則</h1>
      <p>{data?.name}</p>
      {error && (
        <p role="alert" className="text-error">
          {(error as Error).message}
        </p>
      )}
      {ruleError && (
        <p role="alert" className="text-error">
          {(ruleError as Error).message}
        </p>
      )}
      {data && effectiveRule && draft && (
        <section className="space-y-4 rounded-xl border p-4">
          <p className="text-sm">
            版本 {effectiveRule.version} ·{' '}
            {effectiveRule.source === 'EVENT_OVERRIDE' ? '單場覆寫' : '場館預設快照'}
          </p>
          <RuleEditor type={data.type} value={draft} onChange={setDraft} />
          <label className="block text-sm">
            更正原因
            <input
              className="mt-1 w-full rounded border p-2"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {locked && <p className="text-warning text-sm">此賽事已結束，規則不可再修改。</p>}
          {!validPayoutRule(draft, data?.type) && (
            <p className="text-error text-sm">比例或級距無效。</p>
          )}
          <button
            type="button"
            className="rounded bg-primary px-4 py-2 text-primary-content disabled:opacity-50"
            disabled={
              locked || !validPayoutRule(draft, data?.type) || !reason.trim() || update.isPending
            }
            onClick={() => update.mutate()}
          >
            儲存本場新版本
          </button>
          {message && (
            <p role="status" className="text-sm">
              {message}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
